import React, { useCallback, useMemo, useRef, useState } from 'react'
import { ClickAwayListener, IconButton, makeStyles, Popper, Snackbar } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import SydButton from '../../../../../commonDesign/Button'
import OperationalTable from '../../../../../organisms/OperationalTable'
import Loading from '../../../../../molecules/Loading'
import ErrorDisplay from '../../../../../molecules/ErrorDisplay'
import { smartEndpoints } from '../../../../../../api/smartEndpoints'
import PolicyFormDialog from './PolicyFormDialog'

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px var(--padding-md)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  policyCount: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  searchInput: {
    padding: '5px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    outline: 'none',
    width: '200px',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  tableWrap: {
    '& .operational-table > thead > tr > th': {
      fontSize: 'var(--text-size-xs)',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'var(--color-neutral-500)',
      padding: '10px 18px',
      borderBottom: '1px solid var(--color-neutral-200)'
    },
    '& .operational-table > thead': {
      backgroundColor: 'var(--color-neutral-050)'
    },
    '& .operational-table > tbody > tr > td': {
      padding: '10px 18px 10px 18px'
    }
  },
  modeBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    lineHeight: '16px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },
  modeRead: {
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  modeWrite: {
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    color: 'var(--color-danger-700, #b91c1c)'
  },
  enabledDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginRight: '6px',
    verticalAlign: 'middle'
  },
  mono: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-xs)'
  },
  methodBadge: {
    fontWeight: 600,
    fontSize: 'var(--text-size-xs)',
    letterSpacing: '0.3px'
  },
  enabledText: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-600)'
  },
  deleteBtn: {
    color: 'var(--color-neutral-300)',
    transition: 'color var(--transition-duration-short, 0.15s) ease',
    '&:hover': {
      color: 'var(--color-danger-600, #dc2626)',
      backgroundColor: 'var(--color-danger-050, #fef2f2)'
    }
  },
  editBtn: {
    color: 'var(--color-neutral-300)',
    transition: 'color var(--transition-duration-short, 0.15s) ease',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'var(--color-primary-050, #eff6ff)'
    }
  },
  confirmPopover: {
    backgroundColor: 'white',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.15))',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    zIndex: 1300
  },
  confirmText: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-700)',
    fontWeight: 500
  },
  confirmActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end'
  }
}))

function PolicyManagement () {
  const classes = useStyles()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [toast, setToast] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const deleteAnchorRef = useRef(null)

  const { data: policies, isLoading, error, refetch } = smartEndpoints.useListPolicies()
  const createPolicy = smartEndpoints.useCreatePolicy()
  const modifyPolicy = smartEndpoints.useModifyPolicy()
  const removePolicy = smartEndpoints.useRemovePolicy()

  const showToast = useCallback((message, severity = 'success') => {
    setToast({ message, severity })
  }, [])

  const handleAdd = useCallback(() => {
    setEditingPolicy(null)
    setDialogOpen(true)
  }, [])

  const handleEdit = useCallback((policy) => {
    setEditingPolicy(policy)
    setDialogOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setDialogOpen(false)
    setEditingPolicy(null)
  }, [])

  const handleSubmit = useCallback(async (formData) => {
    setSaving(true)
    try {
      if (formData.programPolicyId) {
        await modifyPolicy.mutateAsync({
          programPolicyId: formData.programPolicyId,
          policyType: formData.policyType,
          policyStatement: formData.policyStatement
        })
        showToast('Policy updated')
      } else {
        await createPolicy.mutateAsync({
          programType: formData.programType,
          policyType: formData.policyType,
          policyStatement: formData.policyStatement
        })
        showToast('Policy created')
      }
      handleClose()
    } catch (err) {
      showToast(err?.message || 'Failed to save policy', 'error')
    } finally {
      setSaving(false)
    }
  }, [createPolicy, modifyPolicy, handleClose, showToast])

  const handleDeleteClick = useCallback((programPolicyId, anchorEl) => {
    setConfirmDelete({ programPolicyId, anchorEl })
  }, [])

  const handleConfirmRemove = useCallback(async () => {
    if (!confirmDelete) return
    const { programPolicyId } = confirmDelete
    setConfirmDelete(null)
    setDeletingId(programPolicyId)
    try {
      await removePolicy.mutateAsync({ programPolicyId })
      showToast('Policy removed')
    } catch (err) {
      showToast(err?.message || 'Failed to remove policy', 'error')
    } finally {
      setDeletingId(null)
    }
  }, [confirmDelete, removePolicy, showToast])

  const handleCancelRemove = useCallback(() => {
    setConfirmDelete(null)
  }, [])

  const tableData = useMemo(() => {
    if (!policies) return []
    return [...policies]
      .map(p => {
        let stmt = p.policyStatement || {}
        if (typeof p.policyStatement === 'string') {
          try { stmt = JSON.parse(p.policyStatement) } catch { stmt = {} }
        }
        return {
          ...p,
          _stmt: stmt,
          service: stmt.service || '',
          pathPattern: stmt.pathPattern || '',
          method: stmt.method || '',
          mode: stmt.mode || '',
          enabled: stmt.enabled !== false
        }
      })
      .sort((a, b) => a.service.localeCompare(b.service) || a.pathPattern.localeCompare(b.pathPattern))
  }, [policies])

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => [
    { id: 'policyType', accessor: 'policyType', Header: 'Policy Type' },
    {
      id: 'service',
      accessor: 'service',
      Header: 'Service',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    {
      id: 'pathPattern',
      accessor: 'pathPattern',
      Header: 'Path Pattern',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    {
      id: 'method',
      accessor: 'method',
      Header: 'Method',
      Cell: ({ value }) => <span className={classes.methodBadge}>{value}</span>
    },
    {
      id: 'mode',
      accessor: 'mode',
      Header: 'Mode',
      Cell: ({ value }) => (
        <span className={`${classes.modeBadge} ${value === 'write' ? classes.modeWrite : classes.modeRead}`}>
          {value}
        </span>
      )
    },
    {
      id: 'enabled',
      accessor: 'enabled',
      Header: 'Enabled',
      Cell: ({ value }) => (
        <>
          <span
            className={classes.enabledDot}
            style={{ backgroundColor: value ? 'var(--color-success-500, #22c55e)' : 'var(--color-neutral-300)' }}
          />
          <span className={classes.enabledText}>{value ? 'Yes' : 'No'}</span>
        </>
      )
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => {
        const isDeleting = deletingId === row.original.programPolicyId
        return (
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: '2px' }}>
            <IconButton
              size='small'
              className={classes.editBtn}
              onClick={() => handleEdit(row.original)}
              title='Edit policy'
              disabled={isDeleting}
            >
              <EditIcon fontSize='small' />
            </IconButton>
            <IconButton
              size='small'
              className={classes.deleteBtn}
              onClick={(e) => handleDeleteClick(row.original.programPolicyId, e.currentTarget)}
              title='Remove policy'
              disabled={isDeleting}
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </div>
        )
      }
    }
  ], [classes, handleEdit, handleDeleteClick, deletingId])
  /* eslint-enable react/prop-types */

  const defaultSort = useMemo(() => [{ id: 'service', desc: false }], [])

  if (error) {
    return <ErrorDisplay error={error} onRetry={refetch} />
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <div className={classes.toolbar}>
        <span className={classes.policyCount}>
          {tableData.length} {tableData.length === 1 ? 'policy' : 'policies'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            className={classes.searchInput}
            placeholder='Search policies…'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <SydButton size='sm' variant='primary' icon='add' onClick={handleAdd}>Add Policy</SydButton>
        </div>
      </div>
      <div className={classes.tableWrap}>
        <OperationalTable
          variant='blank'
          itemName='Policies'
          columns={columns}
          defaultSort={defaultSort}
          data={tableData}
          searchText={searchText}
        />
      </div>
      <Popper
        open={!!confirmDelete}
        anchorEl={confirmDelete?.anchorEl}
        placement='bottom-end'
        style={{ zIndex: 1300 }}
        ref={deleteAnchorRef}
      >
        <ClickAwayListener onClickAway={handleCancelRemove}>
          <div className={classes.confirmPopover}>
            <span className={classes.confirmText}>Remove this policy?</span>
            <div className={classes.confirmActions}>
              <SydButton size='sm' variant='ghost' onClick={handleCancelRemove}>Cancel</SydButton>
              <SydButton size='sm' variant='primary' priority='warning' onClick={handleConfirmRemove}>Remove</SydButton>
            </div>
          </div>
        </ClickAwayListener>
      </Popper>
      <PolicyFormDialog
        open={dialogOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        editingPolicy={editingPolicy}
        saving={saving}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </>
  )
}

export default PolicyManagement
